/**
 * Pino-compatible structured logger for browser (zero dependencies).
 *
 * Usage:
 *   import { logger } from '../lib/logger.js';
 *   logger.info({ userId: 1 }, 'User loaded');
 *   logger.error({ err }, 'Request failed');
 *
 * Level resolution (lowest → highest):
 *   trace(10) debug(20) info(30) warn(40) error(50) fatal(60)
 *
 * Runtime level:
 *   - VITE_LOG_LEVEL env var (build-time)
 *   - Default: 'debug' in dev, 'warn' in production
 */

type Level = 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace';
type Meta = Record<string, unknown>;

const LEVELS: Record<Level, number> = {
  fatal: 60,
  error: 50,
  warn:  40,
  info:  30,
  debug: 20,
  trace: 10,
};

function resolveLevel(): Level {
  const env = (import.meta.env?.VITE_LOG_LEVEL as Level | undefined);
  if (env && env in LEVELS) return env;
  return import.meta.env?.DEV ? 'debug' : 'warn';
}

const currentLevel = LEVELS[resolveLevel()];

export interface Logger {
  fatal(meta: Meta, msg: string): void;
  fatal(msg: string): void;
  error(meta: Meta, msg: string): void;
  error(msg: string): void;
  warn(meta: Meta, msg: string): void;
  warn(msg: string): void;
  info(meta: Meta, msg: string): void;
  info(msg: string): void;
  debug(meta: Meta, msg: string): void;
  debug(msg: string): void;
  trace(meta: Meta, msg: string): void;
  trace(msg: string): void;
  child(bindings: Meta): Logger;
}

function makeLogger(bindings: Meta = {}): Logger {
  const log = (level: Level, a: Meta | string, b?: string) => {
    if (LEVELS[level] < currentLevel) return;
    const [meta, msg] = typeof a === 'string' ? [{}, a] : [a, b ?? ''];
    const entry = { time: Date.now(), level, msg, ...bindings, ...meta };
    const method = (
      level === 'fatal' || level === 'error' ? 'error' :
      level === 'warn'                        ? 'warn'  :
      level === 'info'                        ? 'info'  :
      'debug'
    ) as 'error' | 'warn' | 'info' | 'debug';
    console[method](JSON.stringify(entry)); // intentional: this is the logger sink
  };

  return {
    fatal: (a: Meta | string, b?: string) => log('fatal', a, b),
    error: (a: Meta | string, b?: string) => log('error', a, b),
    warn:  (a: Meta | string, b?: string) => log('warn',  a, b),
    info:  (a: Meta | string, b?: string) => log('info',  a, b),
    debug: (a: Meta | string, b?: string) => log('debug', a, b),
    trace: (a: Meta | string, b?: string) => log('trace', a, b),
    child: (bind: Meta) => makeLogger({ ...bindings, ...bind }),
  };
}

export const logger = makeLogger();
